// FitText — fits text inside its fixed-size parent by shrinking the font
// (binary search between minPx and a max derived from the box), allowing
// wrapping up to maxLines. Ellipsis only if it still overflows at minPx.
// `lines` renders several stacked lines (e.g. two players) sized together.
import React, { useCallback, useLayoutEffect, useRef } from 'react';

export default function FitText({ text, lines, maxPx, maxRatio = 0.45, maxWRatio = Infinity, minPx = 10, maxLines = 2, singleLineFloor = 0.85, className = '', style, testid }) {
  const boxRef = useRef(null);
  const elRef = useRef(null);
  const content = lines ? lines.join('\n') : text;

  const fit = useCallback(() => {
    const box = boxRef.current; const el = elRef.current;
    if (!box || !el) return;
    const W = box.clientWidth; const H = box.clientHeight;
    if (!W || !H) return;
    const top = Math.max(minPx, Math.min(maxPx ?? H * maxRatio, W * maxWRatio));
    el.style.webkitLineClamp = 'unset';
    el.style.display = 'block';
    const fits = (px) => {
      el.style.fontSize = `${px}px`;
      const maxH = Math.min(H, px * 1.12 * maxLines + 2);
      return el.scrollHeight <= maxH + 1 && el.scrollWidth <= W + 1;
    };
    const search = (from, to) => {
      let lo = from; let hi = to; let best = null;
      if (fits(hi)) return hi;
      if (!fits(lo)) return null;
      best = lo;
      for (let i = 0; i < 6 && hi - lo > 0.5; i += 1) {
        const mid = (lo + hi) / 2;
        if (fits(mid)) { best = mid; lo = mid; } else { hi = mid; }
      }
      return best;
    };
    // Phase 1: no wrapping (each line stays whole). Phase 2: allow wrapping.
    el.style.whiteSpace = 'nowrap';
    const floor = lines ? minPx : (maxLines > 1 ? top * singleLineFloor : minPx);
    let best = search(floor, top);
    if (best == null) {
      el.style.whiteSpace = 'normal';
      best = search(minPx, top);
    }
    if (best == null) best = minPx;
    el.style.fontSize = `${best}px`;
    if (!fits(best)) {
      el.style.display = '-webkit-box';
      el.style.webkitBoxOrient = 'vertical';
      el.style.webkitLineClamp = String(maxLines);
    }
  }, [maxPx, maxRatio, maxWRatio, minPx, maxLines, singleLineFloor, lines]);

  useLayoutEffect(() => {
    fit();
    const box = boxRef.current;
    if (!box || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => fit());
    ro.observe(box);
    let cancelled = false;
    if (document.fonts?.ready) document.fonts.ready.then(() => { if (!cancelled) fit(); });
    return () => { cancelled = true; ro.disconnect(); };
  }, [fit, content]);

  return (
    <div ref={boxRef} className="w-full h-full min-w-0 min-h-0 flex items-center overflow-hidden">
      <div
        ref={elRef}
        className={`w-full overflow-hidden break-words ${className}`}
        style={{ lineHeight: 1.12, ...style }}
        data-testid={testid}
        title={content || undefined}
      >
        {lines ? lines.map((l, i) => <div key={i}>{l}</div>) : text}
      </div>
    </div>
  );
}
